// 入席与房间大厅：创建会话 → 创建/加入房间 → 准备 → 开局。

import { useEffect, useState } from "react";

import { tip } from "./assets.tsx";
import { MODE_LABEL } from "./glossary.ts";
import { api } from "./api.ts";
import { table } from "./store.ts";
import type { Room, Session } from "./types.ts";

export function Lobby({ me, room }: { me: Session; room: Room | null }) {
  if (room) return <WaitingRoom room={room} me={me} />;
  return <RoomBrowser me={me} />;
}

function RoomBrowser({ me }: { me: Session }) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [name, setName] = useState(`${me.name} 的宴席`);
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [isPublic, setIsPublic] = useState(true);
  const [mode, setMode] = useState("normal");
  const [abilities, setAbilities] = useState(true);
  const [seed, setSeed] = useState("");
  const [joinId, setJoinId] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const data = await api.listRooms(me.token);
      setRooms(data.rooms ?? []);
    } catch (error) {
      table.fail("房间列表拉取失败", error);
    }
  };

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(timer);
  }, [me.token]);

  const guard = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch (error) {
      table.fail("操作失败", error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lobby">
      <section className="lobby-block">
        <h2 className="section-title">開宴</h2>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            void guard(() =>
              table.createRoom({
                name: name.trim() || "無名の宴",
                maxPlayers,
                isPublic,
                mode,
                abilities,
                seed: seed.trim() === "" ? null : Number(seed),
              }),
            );
          }}
        >
          <label>
            宴席之名
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength={40} />
          </label>
          <label>
            定员
            <select
              value={maxPlayers}
              onChange={(event) => setMaxPlayers(Number(event.target.value))}
            >
              {[3, 4, 5, 6, 7, 8].map((count) => (
                <option key={count} value={count}>
                  {count} 人
                </option>
              ))}
            </select>
          </label>
          <label>
            难度
            <select value={mode} onChange={(event) => setMode(event.target.value)}>
              <option value="normal">通常</option>
              <option value="hard">困難</option>
              <option value="lunatic">極難</option>
              <option value="basic">基礎（特殊能力なし）</option>
            </select>
          </label>
          <label>
            种子（可空）
            <input
              value={seed}
              onChange={(event) => setSeed(event.target.value.replace(/[^0-9-]/g, ""))}
              placeholder="随机"
              inputMode="numeric"
            />
          </label>
          <label className="is-check">
            <input
              type="checkbox"
              checked={abilities}
              onChange={(event) => setAbilities(event.target.checked)}
            />
            启用特殊能力
          </label>
          <label className="is-check">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(event) => setIsPublic(event.target.checked)}
            />
            公开房间
          </label>
          <button type="submit" className="is-primary" disabled={busy}>
            開宴
          </button>
        </form>
      </section>

      <section className="lobby-block">
        <h2 className="section-title">入席</h2>
        <form
          className="join-row"
          onSubmit={(event) => {
            event.preventDefault();
            if (joinId.trim() === "") return;
            void guard(() => table.joinRoom(joinId));
          }}
        >
          <input
            value={joinId}
            onChange={(event) => setJoinId(event.target.value)}
            placeholder="房间号 r-…"
          />
          <button type="submit" disabled={busy}>
            参加
          </button>
        </form>
        <ul className="room-list">
          {rooms.map((entry) => (
            <li key={entry.room_id}>
              <span className="room-name">{entry.name}</span>
              <span className="room-meta">
                {entry.players.length}/{entry.max_players} 人 ·{" "}
                {MODE_LABEL[entry.ruleset.mode] ?? "規則不明"}
                {entry.ruleset.abilities ? " · 能力" : ""}
              </span>
              <code>{entry.room_id}</code>
              <button type="button" onClick={() => void guard(() => table.joinRoom(entry.room_id))}>
                入席
              </button>
            </li>
          ))}
          {rooms.length === 0 && <li className="is-empty">暂无公开的宴席</li>}
        </ul>
      </section>
    </div>
  );
}

function WaitingRoom({ room, me }: { room: Room; me: Session }) {
  const [botBusy, setBotBusy] = useState(false);
  const mine = room.players.find((player) => player.player_id === me.player_id);
  const isHost = room.host_player_id === me.player_id;
  const readyEnough = room.players.length >= 3 && room.players.every((player) => player.ready);
  const botCount = room.players.filter((player) => player.bot).length;
  const openSeats = Math.max(0, room.max_players - room.players.length);
  const updateBots = async (count: number): Promise<void> => {
    setBotBusy(true);
    try {
      await table.setBots(count);
    } catch (error) {
      table.fail("测试 Bot 调整失败", error);
    } finally {
      setBotBusy(false);
    }
  };
  return (
    <div className="lobby">
      <section className="lobby-block">
        <h2 className="section-title">待機</h2>
        <p className="room-head">
          {room.name} <code>{room.room_id}</code>
          <span className="room-meta">
            {MODE_LABEL[room.ruleset.mode] ?? "規則不明"}
            {room.ruleset.abilities ? " · 能力" : ""} · {room.players.length}/{room.max_players}
          </span>
        </p>
        <ul className="seat-list">
          {room.players.map((player) => (
            <li
              key={player.player_id}
              className={`${player.ready ? "is-ready" : ""}${player.bot ? " is-bot" : ""}`}
            >
              <span className="seat-no">{player.seat + 1}</span>
              <span className="seat-name">
                {player.name}
                {player.player_id === me.player_id ? "（自分）" : ""}
                {player.bot && <small className="bot-badge">BOT</small>}
              </span>
              <span className={`seat-online${player.online ? " is-on" : ""}`}>
                {player.bot ? "自动操作" : player.online ? "在席" : "離席"}
              </span>
              <span className="seat-ready">
                {player.bot ? "自动准备" : player.ready ? "準備完了" : "未準備"}
              </span>
            </li>
          ))}
        </ul>
        {isHost && (
          <div className="bot-controls" aria-label="测试 Bot 控制">
            <span className="bot-count">
              测试 Bot <strong>{botCount}</strong>
            </span>
            <button
              type="button"
              disabled={botBusy || botCount === 0}
              {...tip("移除最后一个测试 Bot")}
              onClick={() => void updateBots(botCount - 1)}
            >
              移除 Bot
            </button>
            <button
              type="button"
              disabled={botBusy || room.players.length >= room.max_players}
              {...tip("加入一个自动准备、自动完成合法行动的测试 Bot")}
              onClick={() => void updateBots(botCount + 1)}
            >
              添加 Bot
            </button>
            <button
              type="button"
              className="is-primary"
              disabled={botBusy || openSeats === 0}
              {...tip(`一次补入测试 Bot，直到房间满员（${room.max_players} 人）`)}
              onClick={() => void updateBots(botCount + openSeats)}
            >
              补满房间
            </button>
          </div>
        )}
        <div className="room-actions">
          <button
            type="button"
            className={mine?.ready ? "is-ghost" : "is-primary"}
            onClick={() => void table.setReady(!(mine?.ready ?? false)).catch((error: unknown) => table.fail("准备失败", error))}
          >
            {mine?.ready ? "取消准备" : "準備完了"}
          </button>
          {isHost && (
            <button
              type="button"
              className="is-primary"
              disabled={!readyEnough}
              {...tip(readyEnough ? "开局：发角色卡、开局落位、进入第一回合" : "至少 3 人且全员准备后才能开局")}
              onClick={() => void table.start().catch((error: unknown) => table.fail("开局失败", error))}
            >
              開始対局
            </button>
          )}
          <button
            type="button"
            className="is-ghost"
            onClick={() => void table.leaveRoom()}
          >
            離席
          </button>
        </div>
        {!readyEnough && (
          <p className="hint">开局条件：至少 3 人在席，且全员已准备。</p>
        )}
      </section>
    </div>
  );
}
